import * as signalR from '@microsoft/signalr';

import { API_BASE_URL, DEBUG_KEY } from './api';

// `access_token` is the query-param name the DebugKeyMiddleware checks when the header
// can't be set (the WebSocket upgrade handshake from a browser doesn't allow custom headers).
const HUB_URL = `${API_BASE_URL}/hubs/voice?access_token=${encodeURIComponent(DEBUG_KEY)}`;

export type SdpType = 'offer' | 'answer';

export interface VoiceSignalingEvents {
  /** Peer ids are user ids — stable across that rider's reconnects. */
  onPeerJoined: (peerId: string, displayName: string, shouldCreateOffer: boolean) => void;
  onPeerLeft: (peerId: string) => void;
  onReceiveSdp: (fromPeerId: string, sdp: string, sdpType: SdpType) => void;
  onReceiveIce: (fromPeerId: string, candidateJson: string) => void;
  /** A peer in the channel reported its position (speed in m/s, heading in degrees). The
   *  display name rides along so the map label doesn't depend on the WebRTC handshake. */
  onPeerLocation: (
    fromPeerId: string,
    name: string,
    lat: number,
    lon: number,
    speed: number | null,
    heading: number | null,
  ) => void;
  /** A rider in the channel shared a saved route (M2) — fetch it by id to view/save. */
  onRouteShared: (fromPeerId: string, riderName: string, routeId: string, routeName: string) => void;
  /** A rider shared an *unsaved* route — its geometry rides along as an encoded polyline
   *  (client-thinned to fit the hub's receive cap); nothing exists server-side to fetch. */
  onRouteSharedInline: (
    fromPeerId: string,
    riderName: string,
    routeName: string,
    polyline: string,
    durationSeconds: number | null,
  ) => void;
  /** Signaling dropped; SignalR is retrying. Existing peer connections may still carry audio. */
  onReconnecting: () => void;
  /**
   * Signaling is back. Fired just before the automatic channel re-join — the handler can
   * mark current peers as awaiting re-announcement; the re-join replays PeerJoined for
   * every member still in the channel.
   */
  onReconnected: () => void;
  /**
   * The re-join completed and every still-present peer has been re-announced. Peers not
   * seen between onReconnected and this event left during the outage and can be dropped.
   */
  onRejoined: () => void;
  /** The automatic re-join failed, or the connection closed unexpectedly. The session is over. */
  onReconnectFailed: (error: unknown) => void;
}

/**
 * Thin wrapper around the VoiceHub SignalR connection. Owns lifecycle (start/stop/reconnect)
 * and exposes typed methods + event subscriptions. Payloads (SDP, ICE) are opaque strings.
 */
export class VoiceSignaling {
  private readonly connection: signalR.HubConnection;
  private readonly events: VoiceSignalingEvents;
  private joinedChannel: string | null = null;
  /** Set on intentional leave() so onclose isn't reported as a failure. */
  private stopping = false;

  constructor(events: VoiceSignalingEvents) {
    this.events = events;
    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(HUB_URL)
      // Never give up: riders sit in dead zones for minutes at a time, far longer than the
      // default policy's ~30s window. Exponential backoff, capped at a steady 10s.
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (ctx) => Math.min(1000 * 2 ** ctx.previousRetryCount, 10_000),
      })
      .build();

    this.connection.on('PeerJoined', events.onPeerJoined);
    this.connection.on('PeerLeft', events.onPeerLeft);
    this.connection.on('ReceiveSdp', events.onReceiveSdp);
    this.connection.on('ReceiveIce', events.onReceiveIce);
    this.connection.on('PeerLocation', events.onPeerLocation);
    this.connection.on('RouteShared', events.onRouteShared);
    this.connection.on('RouteSharedInline', events.onRouteSharedInline);

    this.connection.onreconnecting(() => events.onReconnecting());
    this.connection.onreconnected(() => void this.rejoin());
    // With an infinite retry policy this only fires on stop() or a non-recoverable error.
    this.connection.onclose((error) => {
      if (!this.stopping) {
        events.onReconnectFailed(error ?? new Error('Voice signaling connection closed'));
      }
    });
  }

  async join(channel: string): Promise<void> {
    if (this.connection.state === signalR.HubConnectionState.Disconnected) {
      await this.connection.start();
    }
    await this.connection.invoke('JoinChannel', channel);
    this.joinedChannel = channel;
  }

  async leave(): Promise<void> {
    this.stopping = true;
    try {
      // Only tell the hub if we actually joined; either way the connection itself must
      // be stopped below — a join that failed mid-way (start succeeded, JoinChannel
      // didn't) would otherwise leak an open connection that retries forever.
      if (this.joinedChannel !== null) {
        await this.connection.invoke('LeaveChannel');
      }
    } finally {
      this.joinedChannel = null;
      await this.connection.stop();
    }
  }

  sendSdp(targetPeerId: string, sdp: string, sdpType: SdpType): Promise<void> {
    return this.connection.invoke('RelaySdp', targetPeerId, sdp, sdpType);
  }

  sendIce(targetPeerId: string, candidateJson: string): Promise<void> {
    return this.connection.invoke('RelayIce', targetPeerId, candidateJson);
  }

  /** Pushes a saved route to everyone else in the channel — they receive RouteShared. */
  shareRoute(routeId: string, routeName: string): Promise<void> {
    if (this.connection.state !== signalR.HubConnectionState.Connected) {
      return Promise.reject(new Error('Not connected'));
    }
    return this.connection.invoke('ShareRoute', routeId, routeName);
  }

  /** Pushes an unsaved route's geometry to the channel — receivers get RouteSharedInline. */
  shareRouteInline(routeName: string, polyline: string, durationSeconds: number | null): Promise<void> {
    if (this.connection.state !== signalR.HubConnectionState.Connected) {
      return Promise.reject(new Error('Not connected'));
    }
    return this.connection.invoke('ShareRouteInline', routeName, polyline, durationSeconds);
  }

  /**
   * Reports our position to the channel. No-op while the connection is down (reconnecting):
   * this fires on a timer, and a failed invoke per tick would just spam rejections — peers
   * grey us out from the silence, which is the designed behaviour for an outage.
   */
  sendLocation(lat: number, lon: number, speed: number | null, heading: number | null): Promise<void> {
    if (this.connection.state !== signalR.HubConnectionState.Connected) {
      return Promise.resolve();
    }
    return this.connection.invoke('UpdateLocation', lat, lon, speed, heading);
  }

  /**
   * After an automatic reconnect the connection id changed, but our identity (user id)
   * didn't — re-running JoinChannel just updates the server's connection mapping and
   * replays the peer list to us. SignalR delivers those PeerJoined events before the
   * invoke resolves (same connection, ordered), so onRejoined fires after the last one.
   */
  private async rejoin(): Promise<void> {
    const channel = this.joinedChannel;
    if (channel === null) return;

    this.events.onReconnected();
    try {
      await this.connection.invoke('JoinChannel', channel);
      this.events.onRejoined();
    } catch (error) {
      this.events.onReconnectFailed(error);
    }
  }
}
